import { useRef, type KeyboardEvent } from 'react';
import type { BrandImportJobView, BrandOnboardingStartMode } from '@mg/shared';
import {
  EDIT_MANUALLY_CHOICE,
  MANUAL_SUGGESTIONS_NOTICE,
  REREAD_CHOICE,
  START_CHOICE_COPY,
} from '../../brand/brandCopy';
import { REREAD_ENABLED } from '../../brand/flags/reread';
import { SUGGESTIONS_ENABLED } from '../../brand/flags/suggestions';
import { useBrandDraft } from '../draft/BrandDraftContext';
import type { WizardMode } from '../steps';
import { DocumentBox } from './DocumentBox';
import { UrlListBox } from './UrlListBox';

/** Revisit's choice once re-reading exists (Release 4). */
export type RevisitChoice = 'reread' | 'manual';

interface RadioOption<V extends string> {
  value: V;
  label: string;
  description: string;
}

const CARD =
  'focus-ring flex flex-col items-start gap-1 rounded-xl border border-line bg-card p-4 text-left disabled:cursor-not-allowed aria-checked:border-2 aria-checked:border-action aria-checked:bg-tint aria-checked:p-[calc(1rem-1px)]';

/**
 * Radio cards with roving focus (the AppearanceCard pattern): Tab enters the group once, at the chosen
 * card, and the arrow keys move the choice and the focus, wrapping.
 */
function RadioCards<V extends string>({
  labelledBy,
  options,
  value,
  onChange,
  disabled,
}: {
  labelledBy: string;
  options: readonly RadioOption<V>[];
  value: V;
  onChange: (next: V) => void;
  disabled: boolean;
}) {
  const cards = useRef(new Map<V, HTMLButtonElement>());
  const move = (from: number, delta: number) => {
    const next = options[(from + delta + options.length) % options.length];
    onChange(next.value);
    cards.current.get(next.value)?.focus();
  };
  const onKeyDown = (index: number) => (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      move(index, 1);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      move(index, -1);
    }
  };
  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className="grid gap-3 md:grid-cols-2">
      {options.map((option, index) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            ref={(el) => {
              if (el) cards.current.set(option.value, el);
              else cards.current.delete(option.value);
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            onKeyDown={onKeyDown(index)}
            className={CARD}
          >
            <span className="text-sm font-semibold text-ink">{option.label}</span>
            <span className="text-sm text-ink-muted">{option.description}</span>
          </button>
        );
      })}
    </div>
  );
}

const START_OPTIONS: readonly RadioOption<BrandOnboardingStartMode>[] = [
  { value: 'UPLOAD', ...START_CHOICE_COPY.upload },
  { value: 'MANUAL', ...START_CHOICE_COPY.manual },
];

const REVISIT_OPTIONS: readonly RadioOption<RevisitChoice>[] = [
  { value: 'reread', ...REREAD_CHOICE },
  { value: 'manual', ...EDIT_MANUALLY_CHOICE },
];

/**
 * Step 1 (spec:85-90; web.md §5.1).
 * - Setup: two radio cards, labelled by the step's title. Upload and prefill (the default) shows the
 *   document box (and, in Release 4, the web-page box); Fill in manually clears what an import filled
 *   in, never what was saved.
 * - Revisit: in Release 3 one static card, Edit manually: nothing changes until a field is edited and
 *   its step saved. Release 4 adds Re-read my sources as a second choice.
 */
export function StartStep({
  mode,
  titleId,
  disabled,
  pendingFile,
  fileError,
  uploading,
  uploadProgress,
  currentDocument,
  pages,
  urls,
  onUrlsChange,
  onChooseFile,
  onRemoveFile,
  revisitChoice,
  onRevisitChoice,
}: {
  mode: WizardMode;
  /** The step title's id: the radio group's label. */
  titleId: string;
  disabled: boolean;
  pendingFile: File | null;
  fileError: string | null;
  uploading: boolean;
  uploadProgress: number | null;
  currentDocument: BrandImportJobView['document'];
  pages: BrandImportJobView['pages'] | undefined;
  urls: string[];
  onUrlsChange: (urls: string[]) => void;
  onChooseFile: (file: File) => void;
  onRemoveFile: () => void;
  revisitChoice: RevisitChoice;
  onRevisitChoice: (choice: RevisitChoice) => void;
}) {
  const { state, dispatch } = useBrandDraft();

  if (mode === 'revisit') {
    if (REREAD_ENABLED) {
      return (
        <RadioCards
          labelledBy={titleId}
          options={REVISIT_OPTIONS}
          value={revisitChoice}
          onChange={onRevisitChoice}
          disabled={disabled}
        />
      );
    }
    return (
      <div className="rounded-xl border-2 border-action bg-tint p-[calc(1rem-1px)]">
        <p className="text-sm font-semibold text-ink">{EDIT_MANUALLY_CHOICE.label}</p>
        <p className="mt-1 text-sm text-ink-muted">{EDIT_MANUALLY_CHOICE.description}</p>
      </div>
    );
  }

  return (
    <>
      <RadioCards
        labelledBy={titleId}
        options={START_OPTIONS}
        value={state.startMode}
        onChange={(next) => {
          if (next !== state.startMode) dispatch({ type: 'setStartMode', mode: next });
        }}
        disabled={disabled}
      />
      {state.startMode === 'UPLOAD' && (
        <>
          <DocumentBox
            pendingFile={pendingFile}
            current={currentDocument ? { fileName: currentDocument.fileName, uploadedAt: currentDocument.uploadedAt } : null}
            error={fileError}
            uploading={uploading}
            uploadProgress={uploadProgress}
            disabled={disabled}
            onChoose={onChooseFile}
            onRemove={onRemoveFile}
          />
          <UrlListBox urls={urls} onChange={onUrlsChange} disabled={disabled} previous={pages} />
        </>
      )}
      {state.startMode === 'MANUAL' && SUGGESTIONS_ENABLED && (
        <p className="text-sm text-ink-muted">{MANUAL_SUGGESTIONS_NOTICE}</p>
      )}
    </>
  );
}
